import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CHECKIN_REASONS } from '../../constants/checkinReasons';
import { dayLabel } from '../../utils/tokyoDate';
import CheckinMemoField from './CheckinMemoField';

/**
 * Bottom sheet: add or fix the 行動記録 of any day in the last month (recent 7 as chips, older via the date field).
 * Answers are against the live (approved) plan; a day recorded under an older plan
 * shows those entries read-only as 「以前の行動」.
 * Rendered into `.kuroco-shell` — inside the scrolling page the tab bar would cover it.
 */
export default function CheckinSheet({ days, oldest, today, recordByDay, offByDay = {}, actions, initialDay, saving, error, onSave, onToggleOff, onClose }) {
  const [day, setDay] = useState(initialDay);
  const [answers, setAnswers] = useState({});
  const [reasons, setReasons] = useState({});
  const [memos, setMemos] = useState({});
  const [localError, setLocalError] = useState(null);

  const saved = recordByDay[day];
  const isAhead = day > today; // a future day: only 休み／出勤 can be set, no record
  const isOff = Boolean(offByDay[day]) && !saved; // a recorded day is a working day, whatever the shift says
  const mark = (d) => (recordByDay[d] ? '記録済み' : offByDay[d] ? '休み' : d > today ? '出勤' : '未記録');
  const byName = {};
  (saved?.entries || []).forEach((e) => { byName[e.actionName] = e; });
  const liveNames = new Set(actions.map((a) => a.name));
  const oldEntries = (saved?.entries || []).filter((e) => !liveNames.has(e.actionName));

  // Load the chosen day's saved answers whenever the date changes.
  useEffect(() => {
    const a = {};
    const r = {};
    const m = {};
    actions.forEach((act) => {
      const e = byName[act.name];
      if (e) {
        a[act.name] = e.status;
        if (e.reason) r[act.name] = e.reason;
        if (e.memo) m[act.name] = e.memo;
      }
    });
    setAnswers(a);
    setReasons(r);
    setMemos(m);
    setLocalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the day or its record changes
  }, [day, saved]);

  const save = () => {
    if (actions.some((a) => !answers[a.name])) {
      setLocalError('すべての行動に「できた／できなかった」を選んでください');
      return;
    }
    setLocalError(null);
    onSave(
      day,
      actions.map((a) => ({
        actionId: a.id,
        actionName: a.name,
        status: answers[a.name],
        reason: answers[a.name] === 'miss' ? reasons[a.name] || null : null,
        memo: answers[a.name] === 'miss' ? (memos[a.name] || '').trim() || null : null,
      }))
    );
  };

  const host = typeof document !== 'undefined' ? document.querySelector('.kuroco-shell') || document.body : null;
  const sheet = (
    <div className="apv-mask" onClick={onClose} role="presentation">
      <div className="apv-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="apv-grab" />
        <div className="apv-sheet-h">
          <h2>{isAhead ? '休みの予定' : '行動記録を追加・修正'}</h2>
          <button type="button" className="apv-x" onClick={onClose} aria-label="閉じる">×</button>
        </div>

        <div className="apv-fl">日付（過去1か月〜1週間先）</div>
        <div className="ck-dates">
          {days.map((d) => (
            <button
              key={d}
              type="button"
              className={`ck-date${d === day ? ' is-on' : ''}`}
              onClick={() => setDay(d)}
            >
              {dayLabel(d, today)}
              <span className="ck-date-mk">{mark(d)}</span>
            </button>
          ))}
        </div>
        <label className="ck-older">
          それより前の日付
          <input
            type="date"
            min={oldest}
            max={days[0]}
            value={days.includes(day) ? '' : day}
            onChange={(e) => {
              const v = e.target.value;
              // The picker can still be typed outside min/max — keep only in-window dates.
              if (v && v >= oldest && v <= today) setDay(v);
            }}
          />
          {!days.includes(day) && (
            <span className="ck-date-mk">{mark(day)}</span>
          )}
        </label>

        {saved && (
          <div className="ck-overwrite">{dayLabel(day)} は記録済みです。保存すると上書きします。</div>
        )}
        {onToggleOff && !saved && (
          <div className="ck-off">
            {isOff
              ? <span>{dayLabel(day, today)} は休みの日です{isAhead ? '' : '（記録は要りません）'}。</span>
              : <span>{isAhead ? '休みの日は通知が止まります。' : '休みの日は、未記録に数えず、通知も止まります。'}</span>}
            <button type="button" className="set-group-edit" disabled={saving} onClick={() => onToggleOff(day, !isOff)}>
              {isOff ? `${dayLabel(day, today)} を出勤に戻す` : `${dayLabel(day, today)} を休みにする`}
            </button>
          </div>
        )}

        {!isAhead && oldEntries.length > 0 && (
          <div className="apv-block">
            <div className="apv-fl">以前の行動（この日の記録）</div>
            {oldEntries.map((e) => (
              <div className="ck-old" key={e.actionName}>
                {e.actionName} — {e.status === 'done' ? 'できた' : 'できなかった'}
              </div>
            ))}
          </div>
        )}

        {!isAhead && actions.map((a, i) => (
          <div className="apv-block" key={a.actionId || a.name}>
            <div className="ck-act">{i + 1}. {a.name}</div>
            <div className="ck-seg">
              <button
                type="button"
                className={answers[a.name] === 'done' ? 'is-yes' : ''}
                onClick={() => setAnswers((p) => ({ ...p, [a.name]: 'done' }))}
              >
                できた
              </button>
              <button
                type="button"
                className={answers[a.name] === 'miss' ? 'is-no' : ''}
                onClick={() => setAnswers((p) => ({ ...p, [a.name]: 'miss' }))}
              >
                できなかった
              </button>
            </div>
            {answers[a.name] === 'miss' && (
              <div className="ck-reasons">
                {CHECKIN_REASONS.map((r) => (
                  <button
                    key={r.label}
                    type="button"
                    className={`ck-reason${reasons[a.name] === r.label ? ' is-on' : ''}`}
                    onClick={() => setReasons((p) => ({ ...p, [a.name]: p[a.name] === r.label ? undefined : r.label }))}
                  >
                    {r.label}
                  </button>
                ))}
                <div className="ck-memo">
                  <CheckinMemoField
                    id={`ck-memo-${i}`}
                    value={memos[a.name]}
                    onChange={(v) => setMemos((p) => ({ ...p, [a.name]: v }))}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="apv-block apv-footer">
          {(localError || error) && <div className="ck-err" role="alert">{localError || error}</div>}
          {isAhead ? (
            <button type="button" className="apv-btn" onClick={onClose}>閉じる</button>
          ) : (
            <button type="button" className="apv-btn" disabled={saving} onClick={save}>
              {saving ? '保存中…' : `${dayLabel(day)} の記録を保存`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
  return host ? createPortal(sheet, host) : sheet;
}
